import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  addUser,
  deleteAvatar,
  deleteUser,
  getAllUsers,
  getSeatUsage,
  getUser,
  updateAvatar,
  updateUser,
  updateUserPreferences,
  type SeatUsage,
} from "api/users";
import { getFormattedDate } from "utils/accounts";

import type { UserState } from "./UserState";

type ApiErrorPayload = {
  status?: number;
  data?: { error?: string; status?: string } | string;
  hasManageUserPermission?: boolean;
};

export type CollaborationUserDetail = Record<string, unknown> & {
  _id?: string;
  email?: string;
  userId?: string;
};

export const collaborationUserKeys = {
  all: ["collaboration-users"] as const,
  list: () => [...collaborationUserKeys.all, "list"] as const,
  listWithTimezone: (timezone?: string) =>
    [...collaborationUserKeys.list(), { timezone: timezone || "UTC" }] as const,
  detail: (email: string) =>
    [...collaborationUserKeys.all, "detail", email.toLowerCase()] as const,
  seatUsage: () => [...collaborationUserKeys.all, "seat-usage"] as const,
};

function asApiPayload(value: unknown): ApiErrorPayload | undefined {
  return value && typeof value === "object"
    ? (value as ApiErrorPayload)
    : undefined;
}

function getApiErrorMessage(value: unknown, fallback: string): string {
  const payload = asApiPayload(value);
  if (payload?.data && typeof payload.data === "object") {
    return payload.data.error || fallback;
  }
  if (typeof payload?.data === "string") return payload.data;
  return fallback;
}

function assertSuccessfulResponse<T>(value: T, fallback: string): T {
  const payload = asApiPayload(value);
  const data = payload?.data;
  if (
    !value ||
    (payload?.status !== undefined && payload.status >= 400) ||
    (data &&
      typeof data === "object" &&
      (data.status === "error" || data.error))
  ) {
    throw new Error(getApiErrorMessage(value, fallback));
  }
  return value;
}

function formatUsers(
  users: UserState[] | undefined,
  timezone?: string
): UserState[] {
  if (!Array.isArray(users)) return [];
  return users.map((user) => ({
    ...user,
    invited_format: getFormattedDate(timezone || "UTC", user.invited_at),
  }));
}

function invalidateCollaborationUsers(
  queryClient: ReturnType<typeof useQueryClient>,
  email?: string
) {
  void queryClient.invalidateQueries({
    queryKey: collaborationUserKeys.list(),
  });
  void queryClient.invalidateQueries({
    queryKey: collaborationUserKeys.seatUsage(),
  });
  if (email) {
    void queryClient.invalidateQueries({
      queryKey: collaborationUserKeys.detail(email),
    });
  }
}

export function useCollaborationUsers(timezone?: string) {
  return useQuery({
    queryKey: collaborationUserKeys.listWithTimezone(timezone),
    queryFn: async () => {
      const response = await getAllUsers();
      const payload = asApiPayload(response);
      if (
        payload?.hasManageUserPermission === false ||
        payload?.status === 403
      ) {
        return { users: [], hasManageUserPermission: false };
      }
      assertSuccessfulResponse(
        response,
        "There was an error getting users for your account."
      );
      return {
        users: formatUsers(response as UserState[], timezone),
        hasManageUserPermission: true,
      };
    },
  });
}

export function useCollaborationSeatUsage({ enabled = true } = {}) {
  return useQuery<SeatUsage | undefined>({
    queryKey: collaborationUserKeys.seatUsage(),
    queryFn: getSeatUsage,
    enabled,
  });
}

export function useCollaborationUser(email?: string) {
  return useQuery({
    queryKey: email
      ? collaborationUserKeys.detail(email)
      : collaborationUserKeys.detail(""),
    enabled: !!email,
    queryFn: () => fetchCollaborationUserByEmail(email!),
  });
}

export async function fetchCollaborationUserByEmail(email: string) {
  const response = await getUser(email);
  return assertSuccessfulResponse(
    response,
    "There was an error getting data for this user."
  ) as CollaborationUserDetail;
}

export function useInviteCollaborationUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      email: string;
      firstName: string;
      lastName: string;
    }) => {
      const response = await addUser(payload);
      return assertSuccessfulResponse(
        response,
        "Failed to invite user - please try again."
      );
    },
    onSuccess: (response, variables) => {
      invalidateCollaborationUsers(queryClient, variables.email);
      toast.success(typeof response === "string" ? response : "User added.");
    },
  });
}

export function useUpdateCollaborationUserAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; ids?: string[] }) => {
      const response = await updateUser(payload);
      return assertSuccessfulResponse(
        response,
        "There was an error updating the user."
      );
    },
    onSuccess: (_response, variables) => {
      invalidateCollaborationUsers(queryClient, variables.email);
      toast.success("User profile information was updated successfully.");
    },
  });
}

export function useUpdateCollaborationUserPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      userId: string;
      email?: string;
      preferences: Record<string, unknown>;
    }) => {
      const response = await updateUserPreferences(payload);
      return assertSuccessfulResponse(
        response,
        "There was an error updating the user preferences."
      );
    },
    onSuccess: (_response, variables) => {
      invalidateCollaborationUsers(queryClient, variables.email);
      toast.success("User preferences updated successfully.");
    },
  });
}

export function useDeleteCollaborationUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string }) => {
      const response = await deleteUser(payload);
      return assertSuccessfulResponse(
        response,
        "There was an error deleting the user."
      );
    },
    onSuccess: (_response, variables) => {
      invalidateCollaborationUsers(queryClient, variables.email);
      toast.success("User removed.");
    },
  });
}

export function useUpdateCollaborationUserAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      email: string;
      url: string;
      id?: string;
    }) => {
      const response = await updateAvatar(payload);
      return assertSuccessfulResponse(
        response,
        "There was an error updating the avatar."
      );
    },
    onSuccess: (_response, variables) => {
      invalidateCollaborationUsers(queryClient, variables.email);
      toast.success("A profile picture was added successfully.");
    },
  });
}

export function useDeleteCollaborationUserAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; id?: string }) => {
      const response = await deleteAvatar(payload);
      return assertSuccessfulResponse(
        response,
        "There was an error deleting the avatar."
      );
    },
    onSuccess: (_response, variables) => {
      invalidateCollaborationUsers(queryClient, variables.email);
      toast.success("The profile picture was deleted.");
    },
  });
}
